import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger
} from '@heroui/react';
import { Check, NavArrowDown, SquareCursor } from 'iconoir-react';
import { FC, useRef, useState } from 'react';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import i18n from '~/common/helpers/i18n';
import WebAnalytics from '~/common/helpers/webAnalytics';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import { AiNativeIcon } from '~/components/icons';

interface ModeSwitchProps {
  goToManualMode: () => void;
  goToDigitalWorkerMode: () => void;
  isDigitalWorkerNavigable?: boolean;
  view?: 'Build' | 'Run' | 'Monitor' | null;
  isDisabled?: boolean;
}

export const ModeSwitch: FC<ModeSwitchProps> = ({
  goToManualMode,
  goToDigitalWorkerMode,
  isDigitalWorkerNavigable = true,
  view = null,
  isDisabled = false
}) => {
  const { AI_NATIVE_ALLOW_ACCESS, AI_NATIVE_MONITORING } = featureFlagConstants;
  const { treatments } = useFeatureFlag([
    AI_NATIVE_ALLOW_ACCESS,
    AI_NATIVE_MONITORING
  ]);
  const isBetaPhaseEnabled =
    treatments[AI_NATIVE_ALLOW_ACCESS]?.treatment === 'on';
  const isAiNativeMonitoringEnabled =
    treatments[AI_NATIVE_MONITORING]?.treatment === 'on';
  const isDigitalWorkerEnabled = isBetaPhaseEnabled && isDigitalWorkerNavigable;

  const [isOpen, setIsOpen] = useState(false);
  const keepOpenRef = useRef(false);

  const trackEvent = (action: string) => {
    if (!view) return;
    WebAnalytics.sendEvent(`[${view}] (Mode Switch) ${action}`, {
      hasAiNativeAccess: isBetaPhaseEnabled
    });
  };

  const handleOpenChange = (open: boolean) => {
    // Clicking the disabled Digital Worker item should log the attempt but
    // keep the dropdown open, so veto the close it would otherwise trigger.
    if (!open && keepOpenRef.current) {
      keepOpenRef.current = false;
      return;
    }
    setIsOpen(open);
    trackEvent(open ? 'Switcher viewed' : 'Dropdown dismissed');
  };

  const getBottomText = () => {
    if (!isBetaPhaseEnabled) return i18n.t('label.digital_worker_team_awaits');
    if (isDigitalWorkerNavigable)
      return i18n.t('label.digital_worker_delegate');
    return isAiNativeMonitoringEnabled
      ? i18n.t('label.digital_worker_coming_soon_run')
      : i18n.t('label.digital_worker_coming_soon_run_monitor');
  };

  const shouldShowLearnMore =
    !isBetaPhaseEnabled ||
    (isBetaPhaseEnabled &&
      !isDigitalWorkerNavigable &&
      (view === 'Run' || (view === 'Monitor' && !isAiNativeMonitoringEnabled)));

  return (
    <Dropdown
      placement='bottom-end'
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DropdownTrigger>
        <Button
          variant='bordered'
          data-testid='header-mode-switch'
          className='h-10 gap-1.5 rounded-full border-1 border-secondary bg-surface pl-3 pr-2 !text-label-lg text-on-surface'
          startContent={<SquareCursor className='icon-small shrink-0' />}
          endContent={<NavArrowDown className='icon-small shrink-0' />}
        >
          {i18n.t('label.manual_mode')}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={i18n.t('label.switch_mode')!}
        classNames={{ base: 'w-[260px] !text-label-lg' }}
        bottomContent={
          <p
            data-testid='mode-switch-bottom-text'
            className='px-2 pb-1 pt-2 text-[12px] font-medium leading-[17px] tracking-[0.25px] text-on-surface-variant'
          >
            {getBottomText()}
            {shouldShowLearnMore && (
              <a
                href={i18n.t('action.ai_native_docs_url')!}
                target='_blank'
                rel='noreferrer'
                className='ml-1 text-on-surface text-label-lg underline'
                onClick={() => trackEvent('Learn more clicked')}
              >
                {i18n.t('action.learn_more')}
              </a>
            )}
          </p>
        }
      >
        <DropdownSection
          title={i18n.t('label.switch_mode')!}
          classNames={{
            heading:
              'mb-2 block border-b border-outline border-solid px-2 pb-2 pt-1 !text-label-md text-on-surface-variant',
            group: 'space-y-1',
            base: 'relative mb-0'
          }}
        >
          <DropdownItem
            key='manual'
            data-testid='mode-switch-manual'
            onPress={goToManualMode}
            startContent={<SquareCursor className='icon-small shrink-0' />}
            endContent={<Check className='icon-smallest shrink-0' />}
            className='p-2 text-on-surface data-[hover=true]:bg-surface-container'
            classNames={{ title: '!text-label-lg text-on-surface' }}
          >
            {i18n.t('label.manual_mode')}
          </DropdownItem>
          <DropdownItem
            key='digital-worker'
            data-testid='mode-switch-digital-worker'
            isDisabled={isDisabled}
            onPress={() => {
              if (isDigitalWorkerEnabled) {
                trackEvent('Digital Worker selected');
                goToDigitalWorkerMode();
                return;
              }
              keepOpenRef.current = true;
              trackEvent('Attempt to click Digital Worker mode');
            }}
            startContent={<AiNativeIcon size={22} className='shrink-0' />}
            endContent={
              <span className='rounded bg-surface-container-low px-2 py-1 text-[12px] font-medium leading-4 tracking-[0.25px] text-on-primary-container'>
                {i18n.t('label.new')}
              </span>
            }
            className={
              isDigitalWorkerEnabled
                ? 'p-2 text-on-surface-variant data-[hover=true]:bg-surface-container data-[hover=true]:text-on-surface'
                : 'cursor-default p-2 text-on-surface-variant data-[hover=true]:bg-transparent'
            }
            classNames={{
              title: isDigitalWorkerEnabled
                ? '!text-label-lg text-on-surface-variant group-data-[hover=true]:text-on-surface'
                : '!text-label-lg text-on-surface-variant'
            }}
          >
            {i18n.t('label.digital_worker_mode')}
          </DropdownItem>
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
};
